import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Scales, X, Plus, CircleNotch, Check, ShoppingCart, ArrowRight, Heart, ArrowSquareOut } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const regionLabels = { AU: 'Australia', UK: 'United Kingdom', US: 'United States' };

export default function ComparePage() {
  const [allShoes, setAllShoes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [compared, setCompared] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [pickerTab, setPickerTab] = useState('all');
  const [wishlistShoes, setWishlistShoes] = useState([]);
  const { user } = useAuth();
  const { format, region } = useCurrency();
  const location = useLocation();

  useEffect(() => {
    axios.get(`${API}/shoes`, { params: { region } }).then(({ data }) => setAllShoes(data));
    // Fetch wishlist if logged in
    if (user) {
      axios.get(`${API}/wishlist`, { params: { region }, withCredentials: true })
        .then(({ data }) => setWishlistShoes(data))
        .catch(() => {});
    }
  }, [user, region]);

  // Accept pre-selected shoes from wishlist page
  useEffect(() => {
    const preSelected = location.state?.preSelected;
    if (preSelected && preSelected.length >= 2) {
      setSelectedIds(preSelected);
    }
  }, [location.state]);

  useEffect(() => {
    if (selectedIds.length >= 2) {
      setLoading(true);
      axios.post(`${API}/compare`, { shoe_ids: selectedIds, region })
        .then(({ data }) => setCompared(data))
        .finally(() => setLoading(false));
    } else {
      setCompared([]);
    }
  }, [selectedIds, region]);

  const toggleShoe = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(s => s !== id));
    } else if (selectedIds.length < 4) {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const filteredShoes = allShoes.filter(s => {
    const matchSearch = `${s.brand} ${s.model}`.toLowerCase().includes(search.toLowerCase());
    const matchCat = !catFilter || s.category === catFilter;
    return matchSearch && matchCat && !selectedIds.includes(s.shoe_id);
  });

  // Auto-select wishlist tab when opening picker if user has wishlist items
  const openPicker = () => {
    if (user && wishlistShoes.length > 0) {
      setPickerTab('wishlist');
    } else {
      setPickerTab('all');
    }
    setShowPicker(true);
  };

  const categories = [...new Set(allShoes.map(s => s.category))].sort();
  const allFeatures = [...new Set(compared.flatMap(s => s.features || []))];
  const allRegions = [...new Set(compared.flatMap(s => s.regions || []))].sort();

  // Find the "winner" (lowest price overall)
  const lowestOverall = compared.length >= 2
    ? compared.reduce((a, b) => (a.lowest_price || 999) < (b.lowest_price || 999) ? a : b)
    : null;

  return (
    <div className="min-h-screen bg-white" data-testid="compare-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Scales size={32} weight="duotone" className="text-[#FF3B30]" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Side by Side</p>
              <h1 className="font-heading font-black text-3xl tracking-tighter uppercase" data-testid="compare-title">Compare Shoes</h1>
            </div>
          </div>
          {selectedIds.length > 0 && (
            <button onClick={() => setSelectedIds([])} className="text-xs font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors" data-testid="clear-compare">
              Clear All
            </button>
          )}
        </div>

        {/* Selection Bar */}
        <div className="flex gap-3 mb-8 flex-wrap items-center" data-testid="compare-selection">
          {selectedIds.map(id => {
            const shoe = allShoes.find(s => s.shoe_id === id);
            return shoe ? (
              <div key={id} className="flex items-center gap-2 border-2 border-[#0A0A0A] px-3 py-1.5 bg-[#F4F4F5]">
                <img src={shoe.image_url} alt="" className="w-8 h-8 object-cover" />
                <span className="text-sm font-bold">{shoe.brand} {shoe.model}</span>
                <button onClick={() => toggleShoe(id)} className="hover:text-[#FF3B30] ml-1" data-testid={`remove-compare-${id}`}><X size={14} /></button>
              </div>
            ) : null;
          })}
          {selectedIds.length < 4 && (
            <button onClick={openPicker}
              className="flex items-center gap-2 border-2 border-dashed border-[#E4E4E7] px-6 py-2 text-sm font-bold uppercase tracking-wide text-[#52525B] hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
              data-testid="add-shoe-btn">
              <Plus size={16} /> Add Shoe ({selectedIds.length}/4)
            </button>
          )}
        </div>

        {/* Shoe Picker Modal */}
        <AnimatePresence>
          {showPicker && (
            <motion.div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowPicker(false)}>
              <motion.div className="bg-white w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-[#E4E4E7] flex justify-between items-center">
                  <h3 className="font-heading font-bold text-lg">Select Shoes to Compare</h3>
                  <button onClick={() => setShowPicker(false)} data-testid="close-picker"><X size={20} /></button>
                </div>

                {/* Tab: My Wishlist / All Shoes */}
                <div className="flex border-b border-[#E4E4E7]">
                  {user && wishlistShoes.length > 0 && (
                    <button onClick={() => setPickerTab('wishlist')}
                      className={`flex-1 py-3 text-xs font-bold uppercase tracking-wide text-center transition-colors flex items-center justify-center gap-2 ${pickerTab === 'wishlist' ? 'bg-[#FF3B30] text-white' : 'text-[#52525B] hover:bg-[#F4F4F5]'}`}
                      data-testid="picker-tab-wishlist">
                      <Heart size={14} weight={pickerTab === 'wishlist' ? 'fill' : 'duotone'} /> My Wishlist ({wishlistShoes.filter(s => !selectedIds.includes(s.shoe_id)).length})
                    </button>
                  )}
                  <button onClick={() => setPickerTab('all')}
                    className={`flex-1 py-3 text-xs font-bold uppercase tracking-wide text-center transition-colors ${pickerTab === 'all' ? 'bg-[#0A0A0A] text-white' : 'text-[#52525B] hover:bg-[#F4F4F5]'}`}
                    data-testid="picker-tab-all">
                    All Shoes
                  </button>
                </div>

                {/* Filters (only for All Shoes tab) */}
                {pickerTab === 'all' && (
                  <div className="p-4 border-b border-[#E4E4E7] flex gap-3">
                    <input type="text" placeholder="Search brand or model..." value={search} onChange={e => setSearch(e.target.value)}
                      className="flex-1 border-2 border-[#E4E4E7] bg-[#F4F4F5] px-4 py-2 focus:border-[#0A0A0A] outline-none text-sm" data-testid="compare-search" />
                    <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
                      className="border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 text-sm" data-testid="compare-category-filter">
                      <option value="">All Categories</option>
                      {categories.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                )}

                <div className="overflow-y-auto flex-1 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Wishlist Tab */}
                  {pickerTab === 'wishlist' && (
                    <>
                      {wishlistShoes.filter(s => !selectedIds.includes(s.shoe_id)).length === 0 ? (
                        <div className="col-span-2 text-center py-8 text-sm text-[#A1A1AA]">
                          All wishlist shoes are already selected for comparison
                        </div>
                      ) : (
                        wishlistShoes.filter(s => !selectedIds.includes(s.shoe_id)).map(shoe => (
                          <button key={shoe.shoe_id} onClick={() => { toggleShoe(shoe.shoe_id); if (selectedIds.length >= 3) setShowPicker(false); }}
                            className="flex items-center gap-3 p-3 border-2 text-left transition-all border-[#FF3B30]/30 bg-red-50/30 hover:border-[#FF3B30]"
                            data-testid={`picker-wishlist-${shoe.shoe_id}`}>
                            <img src={shoe.image_url} alt={shoe.model} className="w-14 h-14 object-cover flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-[#52525B] font-bold uppercase">{shoe.brand}</p>
                              <p className="text-sm font-bold truncate">{shoe.model}</p>
                              <p className="text-xs text-[#A1A1AA] capitalize">{shoe.category?.replace('_', ' ')} &middot; ${shoe.price_range}</p>
                            </div>
                            <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                              <Heart size={14} weight="fill" className="text-[#FF3B30]" />
                              <Plus size={14} className="text-[#FF3B30]" />
                            </div>
                          </button>
                        ))
                      )}
                    </>
                  )}

                  {/* All Shoes Tab */}
                  {pickerTab === 'all' && filteredShoes.slice(0, 50).map(shoe => (
                    <button key={shoe.shoe_id} onClick={() => { toggleShoe(shoe.shoe_id); if (selectedIds.length >= 3) setShowPicker(false); }}
                      className="flex items-center gap-3 p-3 border-2 text-left transition-all border-[#E4E4E7] hover:border-[#FF3B30]"
                      data-testid={`picker-shoe-${shoe.shoe_id}`}>
                      <img src={shoe.image_url} alt={shoe.model} className="w-14 h-14 object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#52525B] font-bold uppercase">{shoe.brand}</p>
                        <p className="text-sm font-bold truncate">{shoe.model}</p>
                        <p className="text-xs text-[#A1A1AA] capitalize">{shoe.category.replace('_', ' ')} &middot; ${shoe.price_range}</p>
                      </div>
                      <Plus size={16} className="text-[#FF3B30] flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading */}
        {loading && <div className="flex justify-center py-20"><CircleNotch size={48} className="animate-spin text-[#FF3B30]" /></div>}

        {/* Comparison Cards (Visual) */}
        {!loading && compared.length >= 2 && (
          <>
            {/* DESKTOP: card grid */}
            <div className={`hidden sm:grid gap-6 mb-10 ${compared.length === 2 ? 'sm:grid-cols-2' : compared.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4'}`} data-testid="comparison-cards">
              {compared.map(shoe => (
                <div key={shoe.shoe_id} className={`border-2 transition-all ${shoe.shoe_id === lowestOverall?.shoe_id ? 'border-[#FF3B30] ring-2 ring-[#FF3B30]/20' : 'border-[#E4E4E7]'}`}>
                  {shoe.shoe_id === lowestOverall?.shoe_id && (
                    <div className="bg-[#FF3B30] text-white text-center text-xs font-bold uppercase tracking-wide py-1" data-testid="best-price-badge">Best Price</div>
                  )}
                  <div className="p-4">
                    {/* Clickable image → goes to retailer */}
                    <a href={shoe.lowest_url || `/shoe/${shoe.shoe_id}`}
                      target={shoe.lowest_url ? "_blank" : "_self"}
                      rel={shoe.lowest_url ? "noopener noreferrer" : undefined}
                      className="block cursor-pointer group"
                      data-testid={`compare-card-link-${shoe.shoe_id}`}>
                      <img src={shoe.image_url} alt={shoe.model} className="w-full h-40 object-contain mb-4 group-hover:scale-105 transition-transform" />
                      <p className="text-xs text-[#52525B] font-bold uppercase tracking-wide">{shoe.brand}</p>
                      <h3 className="font-heading font-bold text-lg leading-tight group-hover:text-[#FF3B30] transition-colors">{shoe.model}</h3>
                    </a>
                    <p className="text-xs text-[#A1A1AA] capitalize mt-1">{shoe.category.replace('_', ' ')} &middot; {shoe.gender} &middot; {shoe.fit_type} width</p>

                    {shoe.lowest_price && (
                      <a href={shoe.lowest_url || '#'} target="_blank" rel="noopener noreferrer"
                        className="mt-4 bg-[#F4F4F5] p-3 block hover:bg-[#FEF2F2] transition-colors cursor-pointer"
                        data-testid={`compare-price-link-${shoe.shoe_id}`}>
                        <p className="text-xs text-[#52525B] font-bold uppercase">From</p>
                        <p className="font-black text-2xl text-[#FF3B30]">{format(shoe.lowest_price, shoe.lowest_currency)}</p>
                        <p className="text-xs text-[#52525B]">at {shoe.lowest_store} <ArrowSquareOut size={10} className="inline ml-0.5" /></p>
                      </a>
                    )}

                    {/* Regions */}
                    <div className="flex gap-1 mt-3 flex-wrap">
                      {shoe.regions?.map(r => (
                        <span key={r} className="bg-[#F4F4F5] text-[#52525B] px-2 py-0.5 text-xs font-bold">{r}</span>
                      ))}
                      <span className="text-xs text-[#A1A1AA] ml-1">{shoe.store_count} stores</span>
                    </div>

                    {/* Two buttons: Shop Now (retailer) + View All Stores (internal) */}
                    <div className="flex gap-2 mt-4">
                      {shoe.lowest_url && (
                        <a href={shoe.lowest_url} target="_blank" rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white font-bold uppercase text-xs px-4 py-2.5 hover:bg-[#D62828] transition-colors"
                          data-testid={`compare-buy-${shoe.shoe_id}`}>
                          <ShoppingCart size={14} /> Shop Now
                        </a>
                      )}
                      <Link to={`/shoe/${shoe.shoe_id}`}
                        className={`${shoe.lowest_url ? 'flex-1' : 'w-full'} inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white font-bold uppercase text-xs px-4 py-2.5 hover:bg-[#333] transition-colors`}
                        data-testid={`compare-view-${shoe.shoe_id}`}>
                        All Stores <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* MOBILE: compact stacked rows — image + brand + price + Shop */}
            <div className="sm:hidden space-y-3 mb-8" data-testid="comparison-cards-mobile">
              {compared.map(shoe => (
                <div key={shoe.shoe_id} className={`border-2 ${shoe.shoe_id === lowestOverall?.shoe_id ? 'border-[#FF3B30]' : 'border-[#E4E4E7]'}`}>
                  {shoe.shoe_id === lowestOverall?.shoe_id && (
                    <div className="bg-[#FF3B30] text-white text-center text-[10px] font-bold uppercase tracking-wide py-0.5">Best Price</div>
                  )}
                  <div className="flex items-stretch gap-3 p-3">
                    <a href={shoe.lowest_url || `/shoe/${shoe.shoe_id}`}
                      target={shoe.lowest_url ? "_blank" : "_self"}
                      rel={shoe.lowest_url ? "noopener noreferrer" : undefined}
                      className="w-20 flex-shrink-0">
                      <img src={shoe.image_url} alt={shoe.model} className="w-20 h-20 object-contain" />
                    </a>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-[#A1A1AA] font-bold uppercase tracking-wide">{shoe.brand}</p>
                      <p className="text-sm font-bold leading-tight truncate">{shoe.model}</p>
                      {shoe.lowest_price && (
                        <p className="mt-1 font-black text-lg text-[#FF3B30] leading-none">
                          {format(shoe.lowest_price, shoe.lowest_currency)}
                        </p>
                      )}
                      <p className="text-[10px] text-[#A1A1AA] mt-0.5 truncate">at {shoe.lowest_store || '—'}</p>
                    </div>
                    {shoe.lowest_url ? (
                      <a href={shoe.lowest_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center justify-center bg-[#FF3B30] text-white font-bold uppercase text-[10px] px-3 self-stretch hover:bg-[#D62828] transition-colors"
                        data-testid={`compare-buy-mobile-${shoe.shoe_id}`}>
                        Shop
                      </a>
                    ) : (
                      <Link to={`/shoe/${shoe.shoe_id}`}
                        className="inline-flex items-center justify-center bg-[#0A0A0A] text-white font-bold uppercase text-[10px] px-3 self-stretch">
                        View
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Detailed Comparison Table */}
            <h2 className="font-heading font-bold text-xl uppercase tracking-tight mb-4">Detailed Comparison</h2>
            <p className="sm:hidden text-[10px] text-[#A1A1AA] mb-2 uppercase tracking-wide">Swipe table left/right · features hidden on mobile</p>
            <div className="overflow-x-auto border-2 border-[#E4E4E7]" data-testid="comparison-table">
              <table className="w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr>
                    <th className="p-2 sm:p-3 text-left text-[10px] sm:text-xs font-bold uppercase tracking-wide text-[#52525B] bg-[#0A0A0A] text-white border-r border-[#333] w-28 sm:w-40">Feature</th>
                    {compared.map(shoe => (
                      <th key={shoe.shoe_id} className="p-2 sm:p-3 text-center bg-[#0A0A0A] text-white border-r border-[#333] last:border-r-0 min-w-[120px] sm:min-w-[180px]">
                        <a href={shoe.lowest_url || `/shoe/${shoe.shoe_id}`}
                          target={shoe.lowest_url ? "_blank" : "_self"}
                          rel={shoe.lowest_url ? "noopener noreferrer" : undefined}
                          className="hover:text-[#FF3B30] transition-colors"
                          data-testid={`compare-table-header-${shoe.shoe_id}`}>
                          <p className="text-[10px] sm:text-xs text-[#A1A1AA] font-bold uppercase">{shoe.brand}</p>
                          <p className="font-bold text-xs sm:text-sm leading-tight">{shoe.model} <ArrowSquareOut size={10} className="inline" /></p>
                        </a>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="hidden sm:table-row">
                    <td className="p-3 text-xs font-bold uppercase text-[#52525B] border border-[#E4E4E7] bg-[#F4F4F5]">Category</td>
                    {compared.map(s => <td key={s.shoe_id} className="p-3 text-center border border-[#E4E4E7] capitalize">{s.category?.replace('_', ' ')}</td>)}
                  </tr>
                  <tr className="hidden sm:table-row">
                    <td className="p-3 text-xs font-bold uppercase text-[#52525B] border border-[#E4E4E7] bg-[#F4F4F5]">Gender</td>
                    {compared.map(s => <td key={s.shoe_id} className="p-3 text-center border border-[#E4E4E7] capitalize">{s.gender}</td>)}
                  </tr>
                  <tr>
                    <td className="p-2 sm:p-3 text-[10px] sm:text-xs font-bold uppercase text-[#52525B] border border-[#E4E4E7] bg-[#F4F4F5]">Width</td>
                    {compared.map(s => <td key={s.shoe_id} className="p-2 sm:p-3 text-center border border-[#E4E4E7]">{s.fit_type}</td>)}
                  </tr>
                  <tr>
                    <td className="p-2 sm:p-3 text-[10px] sm:text-xs font-bold uppercase text-[#52525B] border border-[#E4E4E7] bg-[#F4F4F5]">MSRP</td>
                    {compared.map(s => <td key={s.shoe_id} className="p-2 sm:p-3 text-center border border-[#E4E4E7] font-bold">${s.price_range}</td>)}
                  </tr>
                  {/* Price by region */}
                  {allRegions.map(region => (
                    <tr key={region}>
                      <td className="p-2 sm:p-3 text-[10px] sm:text-xs font-bold uppercase text-[#52525B] border border-[#E4E4E7] bg-[#F4F4F5]">Best in {region}</td>
                      {compared.map(s => {
                        const rp = s.prices_by_region?.[region];
                        const isLowest = rp && compared.every(other => {
                          const op = other.prices_by_region?.[region];
                          return !op || rp.price <= op.price;
                        });
                        return (
                          <td key={s.shoe_id} className={`p-2 sm:p-3 text-center border border-[#E4E4E7] ${isLowest ? 'bg-green-50' : ''}`}>
                            {rp ? (
                              <a href={rp.product_url || '#'} target="_blank" rel="noopener noreferrer"
                                className="hover:text-[#FF3B30] transition-colors"
                                data-testid={`compare-region-link-${s.shoe_id}-${region}`}>
                                <span className={`font-bold ${isLowest ? 'text-[#10B981]' : ''}`}>{format(rp.price, rp.currency)}</span>
                                <p className="text-[10px] sm:text-xs text-[#A1A1AA] truncate">{rp.store_name} <ArrowSquareOut size={9} className="inline" /></p>
                              </a>
                            ) : (
                              <span className="text-[#E4E4E7]">&mdash;</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="hidden sm:table-row">
                    <td className="p-3 text-xs font-bold uppercase text-[#52525B] border border-[#E4E4E7] bg-[#F4F4F5]">Available In</td>
                    {compared.map(s => (
                      <td key={s.shoe_id} className="p-3 text-center border border-[#E4E4E7]">
                        <div className="flex gap-1 justify-center flex-wrap">{s.regions?.map(r => <span key={r} className="bg-[#F4F4F5] px-2 py-0.5 text-xs font-bold">{r}</span>)}</div>
                        <p className="text-xs text-[#A1A1AA] mt-1">{s.store_count} stores</p>
                      </td>
                    ))}
                  </tr>
                  {/* Features — hidden on mobile to reduce clutter */}
                  {allFeatures.map(feat => (
                    <tr key={feat} className="hidden sm:table-row">
                      <td className="p-3 text-xs font-bold uppercase text-[#52525B] border border-[#E4E4E7] bg-[#F4F4F5]">{feat}</td>
                      {compared.map(s => (
                        <td key={s.shoe_id} className="p-3 text-center border border-[#E4E4E7]">
                          {s.features?.includes(feat) ? <Check size={18} className="text-[#10B981] mx-auto" /> : <span className="text-[#E4E4E7]">&mdash;</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="hidden sm:table-row">
                    <td className="p-3 text-xs font-bold uppercase text-[#52525B] border border-[#E4E4E7] bg-[#F4F4F5]">Description</td>
                    {compared.map(s => <td key={s.shoe_id} className="p-3 text-sm text-[#52525B] border border-[#E4E4E7]">{s.description}</td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}

        {!loading && compared.length < 2 && (
          <div className="text-center py-20 border-2 border-dashed border-[#E4E4E7]" data-testid="compare-empty">
            <Scales size={48} className="text-[#E4E4E7] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0A0A0A] mb-2">Compare Shoes Side by Side</h2>
            <p className="text-sm text-[#52525B] mb-6 max-w-md mx-auto">Select 2-4 shoes to compare features, prices across stores, and availability by region.</p>
            <button onClick={openPicker}
              className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-3 hover:bg-[#D62828] transition-colors"
              data-testid="start-compare-btn">
              <Plus size={16} /> Start Comparing
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
